import React, { useContext, useState, useEffect } from 'react';
import {
  InputField,
  RadioGroupField,
  SelectField,
  useRepeatedSection
} from '@trp-beacon-ui-kit/react-controls';
import styles from './RPSOCRBeneficiaryInfo.css';
import { FlowContext } from '../../core/flow-engine';
import {
  formatSsnForDisplay,
  handleInputFormat
} from '../../flows/shared/utils';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import { beneRelationshipOptions, getBeneTaxIDLabel } from './util';
import { sum, titleCaseForString, withDefault } from '../../core/util';
import { rpsBeneficiaryCatogories } from '../../flows/front-office/shared/utils';
import { middlenameValidation } from '../../flows/shared/validations';

const RPSOCRRenderPrimaryEntitySection = ({
  showPrimaryBeneDistributionError,
  primaryEntityType,
  idx,
  makeName,
  getDefault,
  primaryBeneDistributionAction
}) => {
  const {
    formProps: { primaryBeneSsn, primaryBenePercentDistribution }
  } = useContext(FlowContext);

  const [ssnValue, setSSNValue] = useState('');

  return (
    <div className="qv-row">
      <div className="qv-col">
        <InputField
          title="% of Distribution"
          maxLength="3"
          {...primaryBenePercentDistribution}
          name={makeName(primaryBenePercentDistribution.name)}
          defaultValue={getDefault(primaryBenePercentDistribution.name)}
          onChange={event =>
            primaryBeneDistributionAction(idx, event.target.value)
          }
        />
        {showPrimaryBeneDistributionError && (
          <div className="bene-relationship-error">
            The total for Primary Beneficiaries must equal 100%. Please
            re-enter.
          </div>
        )}
      </div>
      <div className="qv-col">
        <InputField
          title={getBeneTaxIDLabel(
            primaryEntityType[idx],
            rpsBeneficiaryCatogories.INDIVIDUAL
          )}
          placeholder="000-00-0000"
          maxLength="11"
          onChange={evt => handleInputFormat(evt, 'SSN', setSSNValue)}
          {...primaryBeneSsn}
          name={makeName(primaryBeneSsn.name)}
          value={withDefault(
            ssnValue,
            formatSsnForDisplay(getDefault(primaryBeneSsn.name))
          )}
        />
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

const RPSOCRRenderPrimaryBeneRelationsShipDOBInputs = ({
  showPrimaryBeneRelationshipError,
  idx,
  makeName,
  getDefault,
  primaryBeneRelationshipAction
}) => {
  const {
    formProps: { primaryBeneRelationship, primaryBeneDob }
  } = useContext(FlowContext);

  const [dobValue, setDobValue] = useState('');

  return (
    <div className="qv-row">
      <div className="qv-col">
        <SelectField
          title="Relationship"
          {...primaryBeneRelationship}
          name={makeName(primaryBeneRelationship.name)}
          defaultValue={titleCaseForString(
            getDefault(primaryBeneRelationship.name)
          )}
          onChange={event =>
            primaryBeneRelationshipAction(idx, event.target.value)
          }>
          <option hidden value="">
            Select one
          </option>
          {beneRelationshipOptions.map(item => (
            <option key={item} value={item} data-name={item}>
              {item}
            </option>
          ))}
        </SelectField>
        {showPrimaryBeneRelationshipError && (
          <div className="bene-relationship-error">
            Spouse was selected more than once as a beneficiary relationship.
            Please select it once only.
          </div>
        )}
      </div>
      <div className="qv-col">
        <InputField
          type="text"
          title="Date of Birth"
          aria-describedby="hint"
          onChange={evt => handleInputFormat(evt, 'DATE', setDobValue)}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          {...primaryBeneDob}
          name={makeName(primaryBeneDob.name)}
          value={withDefault(dobValue, getDefault(primaryBeneDob.name))}
        />
        <dd id="hint" style={{ display: 'none' }}>
          This is a required field. Please enter a two digit month, a two digit
          day and a four digit year for the Date of Birth value.
        </dd>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

const RPSOCRRenderPrimaryBeneBox = ({
  primaryBeneficiaries = [],
  showPrimaryBeneRelationshipError,
  setShowPrimaryBeneRelationshipError,
  showPrimaryBeneDistributionError,
  setShowPrimaryBeneDistributionError
}) => {
  const {
    formProps: {
      primaryBeneEntityName,
      primaryBeneFirstName,
      primaryBeneMiddleName,
      primaryBeneLastName,
      primaryBeneDob,
      primaryBeneSsn,
      primaryBeneRelationship,
      primaryBenePercentDistribution,
      primaryBeneCategoryType
    }
  } = useContext(FlowContext);

  const [primaryBeneRelationships, setPrimaryBeneRelationships] = useState(
    primaryBeneficiaries.map(person => person.relationship)
  );

  const [primaryBeneDistribution, setPrimaryBeneDistributions] = useState(
    primaryBeneficiaries.map(person => person.percentageDistribution)
  );

  const [primaryEntityType, setPrimaryEntityType] = useState(
    primaryBeneficiaries.map(person => person.beneCategoryType)
  );

  useEffect(() => {
    const totalPrimaryBeneDistributionPercent = [...primaryBeneDistribution];
    const percentSum = sum(totalPrimaryBeneDistributionPercent);

    if (percentSum !== 100) {
      setShowPrimaryBeneDistributionError(true);
    } else {
      setShowPrimaryBeneDistributionError(false);
    }
    return () => {
      setShowPrimaryBeneDistributionError(false);
      setShowPrimaryBeneRelationshipError(false);
    };
  }, []);

  /* (1) updates bene relationship array when a section is removed and
     (2) filters SPOUSE relationships and sets the error state accordingly
  */
  const handleRemovePrimaryBeneRelationshipSection = idx => {
    const filteredPrimaryBeneRelationshipNames = primaryBeneRelationships.filter(
      (_, index) => index !== idx
    );
    const filteredPrimaryBeneRelationSpouse = filteredPrimaryBeneRelationshipNames.filter(
      rel => rel === 'Spouse'
    );
    if (filteredPrimaryBeneRelationSpouse.length < 2) {
      setShowPrimaryBeneRelationshipError(false);
    }
    setPrimaryBeneRelationships(filteredPrimaryBeneRelationshipNames);
  };

  const handleRemovePrimaryBeneDistributionSection = idx => {
    const filteredPrimaryBeneDistributionNames = primaryBeneDistribution.filter(
      (_, index) => index !== idx
    );
    setPrimaryBeneDistributions(filteredPrimaryBeneDistributionNames);

    const newPercentSum = sum(filteredPrimaryBeneDistributionNames);
    if (newPercentSum === 100) {
      setShowPrimaryBeneDistributionError(false);
    } else {
      setShowPrimaryBeneDistributionError(true);
    }
  };

  const defaults = {
    primaryBenes: primaryBeneficiaries.map(person => ({
      [primaryBeneCategoryType.name]: person.beneCategoryType,
      [primaryBeneFirstName.name]: person.name?.firstName,
      [primaryBeneMiddleName.name]: person.name?.middleName,
      [primaryBeneLastName.name]: person.name?.lastName,
      [primaryBeneEntityName.name]: person.entityName,
      [primaryBenePercentDistribution.name]: person.percentageDistribution,
      [primaryBeneDob.name]: person.dateOfBirth,
      [primaryBeneSsn.name]: person.taxID,
      [primaryBeneRelationship.name]: person.relationship
    }))
  };

  const { sections, addSection } = useRepeatedSection('primaryBenes', defaults);

  const primaryBeneRelationshipAction = (idx, value) => {
    const updatedPrimaryBeneRelationships = [...primaryBeneRelationships];
    updatedPrimaryBeneRelationships[idx] = value;

    const filteredPrimaryBeneRelationships = updatedPrimaryBeneRelationships.filter(
      rel => {
        return rel === 'Spouse';
      }
    );
    if (filteredPrimaryBeneRelationships.length >= 2) {
      setShowPrimaryBeneRelationshipError(true);
      setPrimaryBeneRelationships(updatedPrimaryBeneRelationships);
    } else {
      setShowPrimaryBeneRelationshipError(false);
      setPrimaryBeneRelationships(updatedPrimaryBeneRelationships);
    }
  };

  const primaryBeneDistributionAction = (idx, value) => {
    const updatedPrimaryBeneDistributionships = [...primaryBeneDistribution];
    updatedPrimaryBeneDistributionships[idx] = Number(value);

    const percentSum = sum(updatedPrimaryBeneDistributionships);

    if (percentSum !== 100) {
      setShowPrimaryBeneDistributionError(true);
      setPrimaryBeneDistributions(updatedPrimaryBeneDistributionships);
    } else {
      setShowPrimaryBeneDistributionError(false);
      setPrimaryBeneDistributions(updatedPrimaryBeneDistributionships);
    }
  };

  const primaryEntityTypeAction = (idx, value) => {
    const updatedPrimaryEntityTypeAction = [...primaryEntityType];
    updatedPrimaryEntityTypeAction[idx] = value;

    setPrimaryEntityType(updatedPrimaryEntityTypeAction);
  };

  return (
    <>
      {sections.map(({ key, getDefault, makeName, removeSection }, idx) => {
        return (
          <div key={key} className="section">
            <div className="remove-section-button">
              <MFBeaconButton
                id="removePrimaryBeneBtn"
                data-name="removePrimaryBeneBtn"
                noButtonMargin
                type="button"
                onClick={() => {
                  removeSection();
                  handleRemovePrimaryBeneRelationshipSection(idx);
                  handleRemovePrimaryBeneDistributionSection(idx);
                }}
                variant="tertiary"
                label="X"
              />
            </div>
            <div className="review-section">
              <div className="personal-info">
                <div className="qv-row">
                  <div className="bene-option-input">
                    <RadioGroupField
                      title="Beneficiary Type"
                      options={[
                        {
                          title: 'Individual',
                          value: rpsBeneficiaryCatogories.INDIVIDUAL,
                          defaultChecked:
                            primaryEntityType[idx] ===
                            rpsBeneficiaryCatogories.INDIVIDUAL
                        },
                        {
                          title: 'Entity',
                          value: rpsBeneficiaryCatogories.ENTITY,
                          defaultChecked:
                            primaryEntityType[idx] ===
                            rpsBeneficiaryCatogories.ENTITY
                        }
                      ]}
                      vertical
                      onChange={event =>
                        primaryEntityTypeAction(idx, event.target.value)
                      }
                      {...primaryBeneCategoryType}
                      name={makeName(primaryBeneCategoryType.name)}
                    />
                  </div>
                </div>

                {primaryEntityType[idx] ===
                  rpsBeneficiaryCatogories.INDIVIDUAL && (
                  <div className="qv-row">
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="First Name"
                        {...primaryBeneFirstName}
                        name={makeName(primaryBeneFirstName.name)}
                        defaultValue={getDefault(primaryBeneFirstName.name)}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Middle Name"
                        {...primaryBeneMiddleName}
                        name={makeName(primaryBeneMiddleName.name)}
                        defaultValue={getDefault(primaryBeneMiddleName.name)}
                        validation={middlenameValidation}
                      />
                    </div>
                    <div className="qv-col">
                      <InputField
                        type="text"
                        title="Last Name"
                        {...primaryBeneLastName}
                        name={makeName(primaryBeneLastName.name)}
                        defaultValue={getDefault(primaryBeneLastName.name)}
                      />
                    </div>
                  </div>
                )}
                {primaryEntityType[idx] === rpsBeneficiaryCatogories.ENTITY && (
                  <div className="qv-row">
                    <InputField
                      type="text"
                      title="Trust/Entity Name"
                      {...primaryBeneEntityName}
                      name={makeName(primaryBeneEntityName.name)}
                      defaultValue={getDefault(primaryBeneEntityName.name)}
                      maxLength="70"
                    />
                  </div>
                )}

                {primaryEntityType[idx] !== undefined && (
                  <RPSOCRRenderPrimaryEntitySection
                    showPrimaryBeneDistributionError={
                      showPrimaryBeneDistributionError
                    }
                    primaryEntityType={primaryEntityType}
                    idx={idx}
                    makeName={makeName}
                    getDefault={getDefault}
                    primaryBeneDistributionAction={
                      primaryBeneDistributionAction
                    }
                  />
                )}

                {primaryEntityType[idx] !==
                  rpsBeneficiaryCatogories.INDIVIDUAL || (
                  <RPSOCRRenderPrimaryBeneRelationsShipDOBInputs
                    showPrimaryBeneRelationshipError={
                      showPrimaryBeneRelationshipError
                    }
                    idx={idx}
                    makeName={makeName}
                    getDefault={getDefault}
                    primaryBeneRelationshipAction={
                      primaryBeneRelationshipAction
                    }
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div>
        {sections.length < 4 && (
          <MFBeaconButton
            id="addPrimaryBeneBtn"
            data-name="addPrimaryBeneBtn"
            type="button"
            onClick={addSection}
            variant="tertiary"
            label="+ Add primary beneficiary"
          />
        )}
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default RPSOCRRenderPrimaryBeneBox;
